import { Helmet } from 'react-helmet-async'

interface SEOProps {
  title?: string
  description?: string
  canonical?: string
  type?: 'website' | 'article' | 'product'
  image?: string
  imageAlt?: string
  noindex?: boolean
  article?: {
    publishedTime?: string
    modifiedTime?: string
    author?: string
  }
  structuredData?: object | object[]
}

const SITE_NAME = 'JumpstoneAuth'
const DEFAULT_TITLE = 'JumpstoneAuth | Developer-First Authentication Platform'
const DEFAULT_DESCRIPTION = 'JumpstoneAuth is a developer-first authentication platform. Add secure login, SSO, MFA, and user management to your app in minutes, not months.'
const DEFAULT_IMAGE = 'https://jumpstoneauth.com/images/og-image.png'
const SITE_URL = 'https://jumpstoneauth.com'
const TWITTER_HANDLE = '@jumpstoneauth'

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  type = 'website',
  image = DEFAULT_IMAGE,
  imageAlt = 'JumpstoneAuth - Developer-First Authentication Platform',
  noindex = false,
  article,
  structuredData,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : undefined

  // Default organization structured data
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Jumpstone Technology Inc.',
    alternateName: 'JumpstoneAuth',
    url: SITE_URL,
    logo: `${SITE_URL}/images/logo.png`,
    description: 'Developer-first authentication platform for modern applications.',
    foundingDate: '2024',
    founders: [
      {
        '@type': 'Organization',
        name: 'Nanthi Ventures',
        url: 'https://www.nanthiventures.com',
      },
    ],
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CA',
      addressRegion: 'Ontario',
    },
    sameAs: [
      'https://www.linkedin.com/company/jumpstoneauth',
      'https://twitter.com/jumpstoneauth',
      'https://github.com/jumpstoneauth',
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'sales',
        email: 'sales@jumpstoneauth.com',
      },
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'support@jumpstoneauth.com',
      },
    ],
  }

  // Default website structured data
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    publisher: {
      '@type': 'Organization',
      name: 'Jumpstone Technology Inc.',
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/docs?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }

  // Collect all structured data
  const allStructuredData = [
    organizationSchema,
    websiteSchema,
    ...(Array.isArray(structuredData) ? structuredData : structuredData ? [structuredData] : []),
  ]

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:locale" content="en_US" />
      
      {/* Article specific OG tags */}
      {type === 'article' && article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {type === 'article' && article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {type === 'article' && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />
      
      {/* Additional SEO tags */}
      <meta name="author" content="Jumpstone Technology Inc." />
      <meta name="publisher" content="Jumpstone Technology Inc." />
      <meta name="copyright" content="© 2024-2026 Jumpstone Technology Inc. All rights reserved." />
      
      {/* Language and region */}
      <meta name="language" content="English" />
      <meta name="geo.region" content="CA-ON" />
      <meta name="geo.placename" content="Toronto" />
      
      {/* Theme and app */}
      <meta name="theme-color" content="#4f46e5" />
      <meta name="msapplication-TileColor" content="#4f46e5" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(allStructuredData)}
      </script>
    </Helmet>
  )
}

// Pre-built structured data generators for different page types
export const StructuredData = {
  // Software Application schema for the product
  softwareApplication: () => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'JumpstoneAuth',
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'Web, iOS, Android',
    description: 'Developer-first authentication platform with SSO, MFA, and user management.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free tier available - up to 1,000 monthly active users',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '87',
    },
    featureList: [
      'Passwordless authentication',
      'Multi-factor authentication (MFA)',
      'Single Sign-On (SSO)',
      'User management dashboard',
      'Multi-tenant support',
      'Role-based access control',
      'Webhooks and events',
      'SDKs for React, Node.js, Python, Go',
    ],
  }),

  // FAQ schema builder
  faq: (questions: { question: string; answer: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  }),

  // Breadcrumb schema builder
  breadcrumb: (items: { name: string; url: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `https://jumpstoneauth.com${item.url}`,
    })),
  }),

  // Service schema for pricing tiers
  service: (name: string, description: string, price?: string) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    provider: {
      '@type': 'Organization',
      name: 'Jumpstone Technology Inc.',
    },
    ...(price && {
      offers: {
        '@type': 'Offer',
        price,
        priceCurrency: 'USD',
      },
    }),
  }),

  // WebPage schema for generic pages
  webPage: (name: string, description: string, url: string) => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: `https://jumpstoneauth.com${url}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'JumpstoneAuth',
      url: 'https://jumpstoneauth.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Jumpstone Technology Inc.',
    },
  }),
}

export default SEO

